import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import Webcam from 'react-webcam';
import { Card } from '@/components/Card/Card';
import { Modal } from '@/components/Modal/Modal';
import { Table } from '@/components/Table/Table';
import {
  checkEtiquetaAndUpdate,
  checkEtiquetaStatus,
  createEndereco,
  enderecamentoKeys,
  fetchEnderecosComReutilizaveis,
  processImage,
} from '@/services/enderecamentoService';
import type { IEtiquetaEnderecamento } from '@/types/enderecamento';
import { useAnalytics, useErrors, useMetrics } from '@/observability';
import './enderecamento.css';

export const Route = createFileRoute('/_authenticated/movimentacao/enderecamento/')({
  component: EnderecamentoPage,
});

const ETIQUETAS_TABLE_COLUMNS: string[] = [
  'Código de Barras',
  'Código Produto',
  'Descrição',
  'Quantidade',
  'Endereço',
  'Status',
];

function getMessageColor(message: string): string {
  if (message.includes('sucesso') || message.includes('adicionada') || message.includes('finalizado')) {
    return 'green';
  }
  if (message.includes('Erro') || message.includes('Por favor')) {
    return 'red';
  }
  return 'blue';
}

function EnderecamentoPage() {
  const queryClient = useQueryClient();
  const metrics = useMetrics();
  const analytics = useAnalytics();
  const errors = useErrors();
  const [etiquetas, setEtiquetas] = useState<IEtiquetaEnderecamento[]>([]);
  const [message, setMessage] = useState('');
  const [endereco, setEndereco] = useState('');
  const [barcode, setBarcode] = useState('');
  const barcodeInputRef = useRef<HTMLInputElement | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [numCaixas, setNumCaixas] = useState('');
  const [showWebcam, setShowWebcam] = useState(false);
  const webcamRef = useRef<Webcam | null>(null);
  const [showNewAddressPopup, setShowNewAddressPopup] = useState(false);
  const [newEndereco, setNewEndereco] = useState('');
  const [newEnderecoError, setNewEnderecoError] = useState('');
  const [numCaixasError, setNumCaixasError] = useState('');

  const {
    data: enderecoList = [],
    isError: isEnderecosError,
    error: enderecosError,
  } = useQuery({
    queryKey: enderecamentoKeys.enderecos(),
    queryFn: fetchEnderecosComReutilizaveis,
  });

  const checkEtiquetaMutation = useMutation({
    mutationFn: checkEtiquetaStatus,
  });

  const finalizeMutation = useMutation({
    mutationFn: checkEtiquetaAndUpdate,
  });

  const processImageMutation = useMutation({
    mutationFn: processImage,
  });

  const createEnderecoMutation = useMutation({
    mutationFn: createEndereco,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: enderecamentoKeys.enderecos() });
    },
  });

  const enderecosErrorMessage = isEnderecosError
    ? enderecosError instanceof Error
      ? enderecosError.message
      : 'Erro ao carregar endereços'
    : '';

  const displayMessage = message || enderecosErrorMessage;

  const handleStartReading = () => {
    barcodeInputRef.current?.focus();
  };

  const handleOpenFinalizePopup = () => {
    if (!endereco) {
      setMessage('Por favor, selecione um endereço.');
      return;
    }
    setShowPopup(true);
  };

  const handleBarcodeScan = async () => {
    if (!barcode) {
      setMessage('Por favor, insira ou escaneie um código de barras.');
      return;
    }

    if (!endereco) {
      setMessage('Por favor, selecione um endereço válido antes de adicionar uma etiqueta.');
      return;
    }

    try {
      const result = await checkEtiquetaMutation.mutateAsync(barcode);

      if (result.status === 'DISPONIVEL') {
        const exists = etiquetas.some((et) => et.barcode === barcode);
        if (!exists) {
          setEtiquetas((prevEtiquetas) => {
            const updatedEtiquetas: IEtiquetaEnderecamento[] = [
              ...prevEtiquetas,
              {
                barcode,
                codigoprod: result.codigo_produto ?? '',
                descricao: result.descricao ?? '',
                quantidade: result.peso_liquido ?? '',
                endereco,
                status: 'DISPONIVEL',
              },
            ];
            setMessage(
              `Etiqueta adicionada com sucesso! Total: ${updatedEtiquetas.length} etiquetas`,
            );
            return updatedEtiquetas;
          });
        } else {
          setMessage('Etiqueta já está na lista.');
        }
      } else {
        setMessage('Etiqueta já foi baixada ou não está disponível.');
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Erro desconhecido';
      setMessage(`Erro ao verificar a etiqueta: ${msg}`);
    }

    setBarcode('');
    barcodeInputRef.current?.focus();
  };

  const handleFinalize = async () => {
    if (etiquetas.length === 0) {
      setMessage('Nenhuma etiqueta para finalizar.');
      return;
    }

    if (!endereco) {
      setMessage('Por favor, selecione um endereço.');
      return;
    }

    const trimmedNumCaixas = numCaixas.trim();
    if (!trimmedNumCaixas || parseInt(trimmedNumCaixas, 10) !== etiquetas.length) {
      setNumCaixasError(
        `Número de caixas lidas (${etiquetas.length}) não coincide com o número informado (${trimmedNumCaixas || 0}).`,
      );
      return;
    }

    setNumCaixasError('');

    try {
      const dataToSend = etiquetas.map((etiqueta) => ({
        barcode: etiqueta.barcode,
        endereco: etiqueta.endereco,
      }));

      await metrics.measure('enderecamento.finalize', () =>
        finalizeMutation.mutateAsync({ etiquetas: dataToSend }),
      );
      analytics.track('Enderecamento Finalized', { count: dataToSend.length });
      setMessage('Endereçamento finalizado e etiquetas atualizadas.');
      setEtiquetas([]);
      setShowPopup(false);
      setNumCaixas('');
    } catch (error) {
      errors.capture(error, { tags: { source: 'enderecamento.finalize' } });
      const msg = error instanceof Error ? error.message : 'Erro desconhecido';
      setNumCaixasError(`Erro ao finalizar endereçamento: ${msg}`);
    }
  };

  const handleBarcodeInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      void handleBarcodeScan();
    }
  };

  const handleCameraScanner = () => {
    setShowWebcam((prev) => !prev);
  };

  const captureBarcode = useCallback(async () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) {
      setMessage('Erro ao capturar imagem');
      return;
    }

    try {
      const result = await processImageMutation.mutateAsync({ image: imageSrc });
      if (result.success && result.barcode) {
        setBarcode(result.barcode);
        setMessage('Código de barras capturado com sucesso!');
      } else {
        setMessage(`Erro ao capturar código de barras: ${result.error ?? 'desconhecido'}`);
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Erro desconhecido';
      setMessage(`Erro ao processar imagem: ${msg}`);
    }
  }, [processImageMutation]);

  const handleOpenNewAddressPopup = () => {
    setShowNewAddressPopup(true);
  };

  const handleCloseNewAddressPopup = () => {
    setShowNewAddressPopup(false);
    setNewEnderecoError('');
  };

  const handleCloseFinalizePopup = () => {
    setShowPopup(false);
    setNumCaixasError('');
  };

  const handleNewEnderecoSubmit = async () => {
    const trimmedEndereco = newEndereco.trim();
    if (!trimmedEndereco) {
      setNewEnderecoError('Por favor, insira um endereço válido.');
      return;
    }

    setNewEnderecoError('');

    try {
      await createEnderecoMutation.mutateAsync({ endereco: trimmedEndereco });
      setMessage('Endereço cadastrado com sucesso!');
      setEndereco(trimmedEndereco);
      setNewEndereco('');
      setShowNewAddressPopup(false);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Erro desconhecido';
      setNewEnderecoError(`Erro ao cadastrar endereço: ${msg}`);
    }
  };

  const isLoading =
    checkEtiquetaMutation.isPending ||
    finalizeMutation.isPending ||
    processImageMutation.isPending ||
    createEnderecoMutation.isPending;

  return (
    <div className="enderecamento-container">
      <div className="enderecamento-content">
        <Card className="enderecamento-card">
          <Card.Header>
            <h3>Endereçamento de Caixas</h3>
          </Card.Header>

          <Card.Content>
            <div className="enderecamento-form-grid">
              <div className="input-content enderecamento-form-grid__field">
                <label htmlFor="endereco">Selecionar Endereço</label>
                <select
                  id="endereco"
                  value={endereco}
                  onChange={(e) => setEndereco(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">Selecione um endereço</option>
                  {enderecoList.map((item) => (
                    <option
                      key={item.enderecos}
                      value={item.enderecos}
                      className={item.reutilizavel ? 'enderecamento-option-reutilizavel' : undefined}
                    >
                      {item.enderecos}
                    </option>
                  ))}
                </select>
              </div>

              <div className="input-content enderecamento-form-grid__field">
                <label htmlFor="barcode">Código de Barras</label>
                <input
                  type="text"
                  id="barcode"
                  ref={barcodeInputRef}
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  onKeyDown={handleBarcodeInputKeyDown}
                  placeholder="Digite ou escaneie o código de barras"
                  disabled={isLoading}
                />
              </div>

              <div className="enderecamento-form-grid__actions">
                <button
                  type="button"
                  className="clear-filter-button"
                  onClick={handleStartReading}
                  disabled={isLoading}
                >
                  Iniciar Leitura
                </button>
              </div>

              <div className="enderecamento-form-grid__actions">
                <button
                  type="button"
                  className="filter-button"
                  onClick={() => void handleBarcodeScan()}
                  disabled={isLoading}
                >
                  Adicionar Código de Barras
                </button>
              </div>
            </div>

            {showWebcam && (
              <div className="enderecamento-webcam-section">
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  className="enderecamento-webcam"
                />
                <button
                  type="button"
                  className="filter-button"
                  onClick={() => void captureBarcode()}
                  disabled={processImageMutation.isPending}
                >
                  Capturar Código de Barras
                </button>
              </div>
            )}
          </Card.Content>

          <Card.Footer className="enderecamento-card__footer">
            <button
              type="button"
              className="clear-filter-button"
              onClick={handleCameraScanner}
              disabled={isLoading}
            >
              {showWebcam ? 'Fechar Câmera' : 'Usar Câmera'}
            </button>

            <button
              type="button"
              className="filter-button"
              onClick={handleOpenNewAddressPopup}
              disabled={isLoading}
            >
              Cadastrar Endereço
            </button>
          </Card.Footer>
        </Card>

        <div className="controle-estoque-table-container">
          <h3>Etiquetas Escaneadas</h3>

          <div className="controle-estoque-table-scroll">
            <Table className="estoque-table">
              <caption className="sr-only">Etiquetas escaneadas para endereçamento</caption>
              <Table.Header>
                <Table.Row>
                  {ETIQUETAS_TABLE_COLUMNS.map((column, index) => (
                    <Table.Head
                      key={index}
                      scope="col"
                    >
                      {column}
                    </Table.Head>
                  ))}
                </Table.Row>
              </Table.Header>

              <Table.Content>
                {etiquetas.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={ETIQUETAS_TABLE_COLUMNS.length}>
                      Nenhuma etiqueta escaneada
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  etiquetas.map((etiqueta) => (
                    <Table.Row key={etiqueta.barcode}>
                      <Table.Cell>{etiqueta.barcode}</Table.Cell>
                      <Table.Cell>{etiqueta.codigoprod}</Table.Cell>
                      <Table.Cell>{etiqueta.descricao}</Table.Cell>
                      <Table.Cell>{etiqueta.quantidade}</Table.Cell>
                      <Table.Cell>{etiqueta.endereco}</Table.Cell>
                      <Table.Cell>{etiqueta.status}</Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Content>
            </Table>
          </div>
        </div>
      </div>

      <div className="enderecamento-actions">
        {displayMessage && (
          <p
            className="enderecamento-message"
            style={{ color: getMessageColor(displayMessage) }}
          >
            {displayMessage}
          </p>
        )}

        <button
          type="button"
          className="filter-button"
          onClick={handleOpenFinalizePopup}
          disabled={etiquetas.length === 0 || isLoading}
        >
          Finalizar Endereçamento
        </button>
      </div>

      {showNewAddressPopup && (
        <Modal
          className="enderecamento-modal"
          onClose={handleCloseNewAddressPopup}
        >
          <Modal.Header title="Cadastrar Novo Endereço" />

          <Modal.Content>
            <div className="input-content enderecamento-modal__field">
              <label htmlFor="new-endereco">Endereço</label>
              <input
                type="text"
                id="new-endereco"
                value={newEndereco}
                onChange={(e) => {
                  setNewEndereco(e.target.value);
                  if (newEnderecoError) {
                    setNewEnderecoError('');
                  }
                }}
                placeholder="Digite o novo endereço"
                aria-invalid={newEnderecoError ? true : undefined}
                aria-describedby={newEnderecoError ? 'new-endereco-error' : undefined}
              />
              {newEnderecoError && (
                <p
                  id="new-endereco-error"
                  className="enderecamento-modal__error"
                  role="alert"
                >
                  {newEnderecoError}
                </p>
              )}
            </div>
          </Modal.Content>

          <Modal.Footer className="enderecamento-modal__footer">
            <button
              type="button"
              className="clear-filter-button"
              onClick={handleCloseNewAddressPopup}
            >
              Cancelar
            </button>

            <button
              type="button"
              className="filter-button"
              onClick={() => void handleNewEnderecoSubmit()}
              disabled={createEnderecoMutation.isPending}
            >
              Confirmar
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {showPopup && (
        <Modal
          className="enderecamento-modal"
          onClose={handleCloseFinalizePopup}
        >
          <Modal.Header title="Confirmar Endereçamento" />

          <Modal.Content>
            <p className="enderecamento-modal__summary">
              Você está endereçando <strong>{etiquetas.length}</strong> etiquetas para o endereço{' '}
              <strong>{endereco}</strong>.
            </p>

            <div className="input-content enderecamento-modal__field">
              <label htmlFor="numCaixas">Número de Caixas</label>
              <input
                type="number"
                id="numCaixas"
                value={numCaixas}
                onChange={(e) => {
                  setNumCaixas(e.target.value);
                  if (numCaixasError) {
                    setNumCaixasError('');
                  }
                }}
                placeholder="Digite o número de caixas"
                aria-invalid={numCaixasError ? true : undefined}
                aria-describedby={numCaixasError ? 'numCaixas-error' : undefined}
              />
              {numCaixasError && (
                <p
                  id="numCaixas-error"
                  className="enderecamento-modal__error"
                  role="alert"
                >
                  {numCaixasError}
                </p>
              )}
            </div>
          </Modal.Content>

          <Modal.Footer className="enderecamento-modal__footer">
            <button
              type="button"
              className="clear-filter-button"
              onClick={handleCloseFinalizePopup}
            >
              Cancelar
            </button>

            <button
              type="button"
              className="filter-button"
              onClick={() => void handleFinalize()}
              disabled={finalizeMutation.isPending}
            >
              Confirmar
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
